/**
 * workflowDraftSlice — the client-side source of truth for a workflow being EDITED.
 *
 * THE SPINE OF OPTION A → OPTION B.
 * ────────────────────────────────
 * Every structural edit the user makes (renaming a Code node's output key, editing
 * its code, adding/removing inputs/outputs, changing a node's prompts/options, the
 * cosmetic output label) is applied to a `WorkflowDraft` here — a deep copy of the
 * API `WorkflowDefinition` plus the edits. Nothing else in the app mutates the
 * definition.
 *
 * OPTION A (this phase): at Run, `toNodeOverrides(draft)` serializes the edits into
 * the EXISTING `nodeOverrides` shape that `startAudit` already sends. The engine
 * `CodeNode(code=…, outputs=…)` / LLM override channel already accept these, so there
 * is ZERO engine/API contract change. Edits live for the session/run.
 *
 * OPTION B (next phase — documented, not built): the same `WorkflowDraft` is exactly
 * the payload a `PUT /v1/workflows/{id}` persistence endpoint needs ("duplicate this
 * workflow into my account"). The cascade-rename / picker / I/O editor built on top
 * of this slice work unchanged on a saved workflow. See docs/workflow-editor-redesign-plan.md.
 *
 * CONTRACT SAFETY (engine/workflow_rule.md):
 *  - Output KEYS are engine-owned on LLM/standard nodes → never renamed here (the UI
 *    keeps them read-only). The user owns keys ONLY on Code nodes.
 *  - Renaming a Code output is an ATOMIC refactor: the key changes AND every
 *    downstream `{{nodes.<id>.<oldKey>}}` ref (in prompts, code, input descriptions,
 *    and input refs) is rewritten in the same reducer. Invariant: zero stale refs
 *    remain (a stale ref would raise VariableResolutionError at runtime, §3.5). We
 *    assert this in dev.
 *  - A variable pill only ever offers a REAL in-scope handle (see
 *    `selectAvailableHandles`): upstream nodes' declared outputs + the
 *    inputs/domain/gsc namespaces.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  CustomStartField,
  WorkflowDefinition,
  WorkflowDefinitionNode,
  WorkflowLibraryNode,
  WorkflowOp,
  StartFieldMeta,
  DraftFinding,
  FindingKind,
  PortSpec,
  NodeOverride,
  OptionValue,
} from "./auditSlice";
import type { RootState } from "./index";
import {
  rewriteNodeOutputRef,
  referencesNodeOutput,
  parseRefs,
  isValidKey,
  buildRef,
} from "@/components/audit/variables/variableTokens";

// ─── Types ───────────────────────────────────────────────────

/** A workflow being edited: the base definition + the user's structural edits,
 *  living entirely client-side until (Option B) persisted. */
export interface WorkflowDraft {
  /** the workflow type this draft derives from (the base template id). */
  baseType: string;
  /** WHICH instance this draft belongs to: `template:<type>` or the duplicate's
   *  wf_id. Template and duplicates SHARE the type — without this, navigating
   *  template → duplicate → template leaked one instance's edits into the other
   *  (a remove_node for a node the template never had — found in real use). */
  instanceId: string;
  /** the full, deeply-cloned, edited definition. */
  definition: WorkflowDefinition;
  /** true once the user has made any structural edit (drives "unsaved" UI / Option B save). */
  dirty: boolean;
  /** C4 — the typed structural ops accumulated since the last save (add/remove
   *  node, set_ports). The draft definition mirrors them for RENDERING; the ops
   *  are the INTENT the save sends — the engine applies them onto the
   *  authoritative doc (the client never sends a document). Cleared on
   *  save/reset/init. */
  pendingOps: WorkflowOp[];
}

/** The engine linter's verdict on a draft — ONE landing spot for the live
 *  dry-run AND a save's 422, read by canvas badges, the Node-errors panel,
 *  the toolbar counter and the Save gate. Kept OUTSIDE WorkflowDraft on
 *  purpose: the live validator re-runs on draft identity, so writing the
 *  verdict INTO the draft would re-trigger validation forever. */
export interface DraftVerdictState {
  findings: DraftFinding[];
  checking: boolean;
}

interface WorkflowDraftState {
  /** keyed by workflow type. One draft per workflow the user has opened. */
  byType: Record<string, WorkflowDraft>;
  /** linter verdicts, keyed by workflow type (sibling of byType — see
   *  DraftVerdictState for why it must not live inside the draft). */
  verdictByType: Record<string, DraftVerdictState>;
}

const initialState: WorkflowDraftState = { byType: {}, verdictByType: {} };

// ─── Handle catalog (what a pill may reference) ──────────────

export type HandleSourceKind = "start" | "node" | "loop";

/** One selectable handle for the variable picker. */
export interface AvailableHandle {
  /** the raw ref token this inserts, e.g. "{{nodes.fetch.page}}". */
  ref: string;
  /** human label for the row. */
  label: string;
  /** the port/engine type, drives the pill glyph. */
  type: PortSpec["type"];
  /** §B class — whether this handle is safe to drop into a prompt. "blob" handles
   *  must be filtered first; the picker badges them and PromptField warns. Start
   *  namespace handles are scalar by construction → undefined treated as scalar. */
  bounded?: PortSpec["bounded"];
  /** Optional human description shown as an info tooltip in the picker (the "ⓘ" the
   *  big players show, e.g. List Item → "The current element from the list being
   *  iterated over"). Absent → the row's tooltip falls back to the raw ref. */
  description?: string;
  /** grouping. */
  source: { kind: HandleSourceKind; id: string; label: string };
}

/**
 * The fixed "Start" namespace handles every node can reference (the runner's
 * prefilled inputs + read-only namespaces, engine/workflow_rule.md §3.7, §9).
 * These mirror the competitor's "Start" group (Topic / Your Brand / URL).
 */
// Closed-graph (#31): the editor proposes {{nodes.start.*}} handles only. The old
// {{inputs.*}} env-seed and the {{domain.*}} resolver were removed engine-side — a
// ref to either now fails the grammar. The user fills these Start fields at launch.
// The ambient {{gsc.impressions_total}} pill was REMOVED (2026-07-09): a hidden
// environment variable (resolves only when the domain has GSC data), consumed by
// ZERO templates, and redundant — GSC data enters closed-graph via the seven
// explicit GSC nodes, whose outputs the picker already lists. Same class as the
// removed {{domain.*}} channel (§11 #31); the engine namespace stays resolvable
// for back-compat, the editor just stops advertising it.
const START_HANDLES: AvailableHandle[] = [
  { ref: "{{nodes.start.url}}", label: "URL", type: "url", source: { kind: "start", id: "start", label: "Start" } },
  { ref: "{{nodes.start.domain_url}}", label: "Domain URL", type: "url", source: { kind: "start", id: "start", label: "Start" } },
  { ref: "{{nodes.start.brand_name}}", label: "Your Brand", type: "text", source: { kind: "start", id: "start", label: "Start" } },
  { ref: "{{nodes.start.business_model}}", label: "Business Model", type: "text", source: { kind: "start", id: "start", label: "Start" } },
];

/**
 * The per-iteration loop scope a sub-node of an IterationNode body can reference: the
 * current element ({{iter.item}}) and its 0-based position ({{iter.index}}). These are
 * first-class declared handles (engine `iter` namespace) — NOT the legacy run_per_item
 * {{item}}. Only in scope INSIDE a container body (see computeAvailableHandles).
 */
const LOOP_HANDLES: AvailableHandle[] = [
  { ref: "{{iter.item}}", label: "List Item", type: "text",
    description: "The current element from the list being iterated over.",
    source: { kind: "loop", id: "iter", label: "Loop" } },
  { ref: "{{iter.index}}", label: "List Index", type: "number",
    description: "The 0-based position of the current element in the list.",
    source: { kind: "loop", id: "iter", label: "Loop" } },
];

// ─── Helpers ─────────────────────────────────────────────────

const TYPE_FROM_UI: Record<string, PortSpec["type"]> = {
  String: "text", Number: "number", Boolean: "boolean", List: "list", Object: "json",
  text: "text", number: "number", boolean: "boolean", list: "list", json: "json", url: "url",
};

/** Deep clone a definition so the draft never aliases the auditSlice state. */
function cloneDefinition(def: WorkflowDefinition): WorkflowDefinition {
  return (typeof structuredClone === "function"
    ? structuredClone(def)
    : JSON.parse(JSON.stringify(def))) as WorkflowDefinition;
}

function findNode(draft: WorkflowDraft, nodeId: string): WorkflowDefinitionNode | undefined {
  return draft.definition.nodes.find((n) => n.id === nodeId);
}

const isCodeNode = (n: WorkflowDefinitionNode) =>
  (n.engine_label || "").toLowerCase() === "code";

/** Rewrite every `{{nodes.<nodeId>.<oldKey>}}` across the whole draft — prompts,
 *  both code bodies, input descriptions. The ATOMIC half of an output rename,
 *  shared by renameCodeOutput and renameLlmOutput. Returns the rewrite count. */
function rewriteRefsAcrossDraft(
  draft: WorkflowDraft, nodeId: string, oldKey: string, newKey: string
): number {
  let rewrites = 0;
  for (const n of draft.definition.nodes) {
    for (const field of ["system_prompt", "user_prompt", "code_python", "code_javascript"] as const) {
      const v = n[field];
      if (typeof v === "string" && v) {
        const r = rewriteNodeOutputRef(v, nodeId, oldKey, newKey);
        if (r.count) { (n[field] as string) = r.text; rewrites += r.count; }
      }
    }
    for (const inp of n.inputs ?? []) {
      if (inp.description) {
        const r = rewriteNodeOutputRef(inp.description, nodeId, oldKey, newKey);
        if (r.count) { inp.description = r.text; rewrites += r.count; }
      }
    }
    // option VALUES can hold a wired `{{ref}}` too (WirableValueField) — a
    // rename must not leave an orphan pill inside an option.
    for (const o of n.options ?? []) {
      if (typeof o.default === "string" && o.default) {
        const r = rewriteNodeOutputRef(o.default, nodeId, oldKey, newKey);
        if (r.count) { o.default = r.text; rewrites += r.count; }
      }
    }
  }
  return rewrites;
}

/** The producer node id a chapter/input ref points at (`nodes.<id>.<key>`). */
function producerOf(ref: string): string | null {
  const m = /nodes\.([A-Za-z0-9_]+)\./.exec(ref);
  return m ? m[1] : null;
}

/** An End node: the one whose deliverable is the `document` output (the same
 *  rule the settings panel uses to pick the chapter editor). */
function isEndNode(n: WorkflowDefinitionNode): boolean {
  return (n.outputs ?? []).some((p) => p.key === "document");
}

/** Replace an End node's chapters wholesale — mirror (inputs from chapters,
 *  label = title; deps = chapter producers + preserved sequencing deps) AND the
 *  coalesced `set_chapters` op. ONE implementation for the picker and for the
 *  cascade of a node removal, so the two can never drift. */
function applyChapters(
  draft: WorkflowDraft, node: WorkflowDefinitionNode,
  chapters: { ref: string; title?: string }[]
): void {
  const oldProducers = new Set(
    (node.inputs ?? []).map((i) => producerOf(i.description ?? "")).filter(Boolean) as string[]);
  const seqDeps = (node.depends_on ?? []).filter(
    (d) => !oldProducers.has(d) && !(node.branch_deps ?? {})[d]);

  const seen = new Set<string>();
  node.inputs = chapters.map((ch, i) => {
    const bare = ch.ref.replace(/^\{\{\s*|\s*\}\}$/g, "");
    const pid = producerOf(bare) ?? `chapter_${i}`;
    const key0 = `${pid}_${bare.split(".").pop()}`;
    let k = key0;
    for (let n = 2; seen.has(k); n++) k = `${key0}_${n}`;
    seen.add(k);
    return { key: k, type: "text" as PortSpec["type"],
             label: ch.title || bare.split(".").pop() || k,
             description: `From {{${bare}}}` };
  });
  const producers = [...new Set(
    chapters.map((c) => producerOf(c.ref)).filter(Boolean) as string[])];
  node.depends_on = [...producers, ...seqDeps.filter((d) => !producers.includes(d))];
  upsertOp(draft, { op: "set_chapters", node: node.id, chapters });
  draft.dirty = true;
}

/** Queue an op, COALESCING the wholesale-snapshot ops (ten panel tweaks still
 *  travel as ONE op): set_ports per node, set_chapters per node,
 *  set_start_fields singleton, rename_output_label per (node, output). */
function upsertOp(draft: WorkflowDraft, op: WorkflowOp): void {
  if (op.op === "set_ports" || op.op === "set_chapters") {
    const i = draft.pendingOps.findIndex((o) => o.op === op.op && o.node === op.node);
    if (i >= 0) {
      const prev = draft.pendingOps[i] as Record<string, unknown>;
      draft.pendingOps[i] = { ...prev, ...op } as WorkflowOp;
      return;
    }
  }
  if (op.op === "set_start_fields") {
    const i = draft.pendingOps.findIndex((o) => o.op === "set_start_fields");
    if (i >= 0) { draft.pendingOps[i] = op; return; }
  }
  if (op.op === "rename_output_label") {
    const i = draft.pendingOps.findIndex(
      (o) => o.op === "rename_output_label" && o.node === op.node && o.output === op.output
    );
    if (i >= 0) { draft.pendingOps[i] = op; return; }
  }
  draft.pendingOps.push(op);
}

/** "From {{nodes.x.y}}" (an input's description convention) → "nodes.x.y". */
function refFromDescription(desc: string | undefined): string | undefined {
  const m = /\{\{\s*([^}]+?)\s*\}\}/.exec(desc ?? "");
  return m ? m[1].trim() : undefined;
}

/** The wholesale set_ports snapshot for a CODE node's current draft state —
 *  what the engine rebuilds the inputs table + output ports from. */
function codePortsSnapshot(node: WorkflowDefinitionNode): Extract<WorkflowOp, { op: "set_ports" }> {
  return {
    op: "set_ports",
    node: node.id,
    inputs: (node.inputs ?? []).map((i) => {
      const ref = refFromDescription(i.description);
      return ref ? { key: i.key, ref, label: i.label } : { key: i.key, label: i.label };
    }),
    outputs: (node.outputs ?? []).map((o) => ({ key: o.key, type: o.type })),
  };
}

/** Transitive set of node ids this node depends on (so the picker only offers
 *  handles that have actually run before it). Cycle-safe. */
function upstreamClosure(def: WorkflowDefinition, nodeId: string): Set<string> {
  const byId = new Map(def.nodes.map((n) => [n.id, n]));
  const seen = new Set<string>();
  const stack = [...(byId.get(nodeId)?.depends_on ?? [])];
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const d of byId.get(id)?.depends_on ?? []) stack.push(d);
  }
  return seen;
}

// ─── Slice ───────────────────────────────────────────────────

const slice = createSlice({
  name: "workflowDraft",
  initialState,
  reducers: {
    /** Initialize (or reset) a draft from the API definition. Idempotent unless `force`. */
    initDraft: (
      state,
      action: PayloadAction<{ definition: WorkflowDefinition; instanceId?: string; force?: boolean }>
    ) => {
      const { definition, force } = action.payload;
      const type = definition.type;
      const instanceId = action.payload.instanceId ?? `template:${type}`;
      const existing = state.byType[type];
      // re-init when FORCED or when the draft belongs to a DIFFERENT instance
      // (template vs duplicate share the type — never leak edits across).
      if (!existing || force || existing.instanceId !== instanceId) {
        state.byType[type] = {
          baseType: type,
          instanceId,
          definition: cloneDefinition(definition),
          dirty: false,
          pendingOps: [],
        };
        state.verdictByType[type] = { findings: [], checking: false };
      }
    },

    /** Discard all edits and re-clone from the base definition. */
    resetDraft: (state, action: PayloadAction<{ type: string; definition: WorkflowDefinition; instanceId?: string }>) => {
      const { type, definition } = action.payload;
      state.byType[type] = {
        baseType: type,
        instanceId: action.payload.instanceId
          ?? state.byType[type]?.instanceId ?? `template:${type}`,
        definition: cloneDefinition(definition),
        dirty: false, pendingOps: [],
      };
      state.verdictByType[type] = { findings: [], checking: false };
    },

    /**
     * Mark the draft as SAVED — clear the dirty flag while KEEPING the current
     * edits (they are now the persisted baseline). Called after a successful
     * in-place save so the "Save workflow" button reverts to "Run analysis".
     * Pending ops are cleared too: the engine has applied them (the post-save
     * refetch replaces the definition with the authoritative one).
     */
    /** AUTOSAVE restore (boot-time): adopt persisted dirty drafts. Existing
     *  in-memory drafts win (hydration runs once, before any page init — but
     *  never clobber live work on a race). The page's own initDraft keeps a
     *  hydrated draft whenever the instanceId matches, so restoration needs no
     *  page-side wiring. */
    hydrateDrafts: (state, action: PayloadAction<{ drafts: Record<string, WorkflowDraft> }>) => {
      for (const [type, draft] of Object.entries(action.payload.drafts)) {
        if (!state.byType[type]) {
          state.byType[type] = draft;
          state.verdictByType[type] = { findings: [], checking: false };
        }
      }
    },

    markDraftSaved: (state, action: PayloadAction<{ type: string }>) => {
      const draft = state.byType[action.payload.type];
      if (draft) { draft.dirty = false; draft.pendingOps = []; }
    },

    /** The engine linter's verdict on this draft — ONE landing spot for both the
     *  debounced live dry-run and a save's 422 findings, so canvas badges, the
     *  Node-errors panel, the toolbar counter and the Save gate never disagree. */
    setDraftFindings: (
      state,
      action: PayloadAction<{ type: string; findings?: DraftFinding[]; checking?: boolean }>
    ) => {
      const v = (state.verdictByType[action.payload.type] ??= { findings: [], checking: false });
      if (action.payload.findings !== undefined) v.findings = action.payload.findings;
      if (action.payload.checking !== undefined) v.checking = action.payload.checking;
    },

    /**
     * SERVER RECONCILIATION — adopt the engine's projected definition of the
     * applied draft (returned by the live validator). The optimistic mirrors
     * give instant feedback; THIS makes the draft converge to the engine's
     * truth (~1s), killing the mirror-drift bug class (stale render_as port,
     * label side-effects, order/connections recompute, …). Guarded by an
     * ops-snapshot: a stale response (the user kept editing) is DISCARDED.
     */
    adoptEngineDefinition: (
      state,
      action: PayloadAction<{ type: string; definition: WorkflowDefinition;
                              expectedOpsJson: string }>
    ) => {
      const { type, definition, expectedOpsJson } = action.payload;
      const draft = state.byType[type];
      if (!draft) return;
      if (JSON.stringify(draft.pendingOps) !== expectedOpsJson) return; // stale
      // PUNTO FISSO. Una proiezione identica a quella gia' in mano NON viene
      // riassegnata. Riassegnarla produceva un draft nuovo a contenuto uguale;
      // il controllo vivo dipende dal draft, quindi ripartiva, rivalidava e
      // riadottava: una /validate ogni ~1,7 s finche' la tela restava aperta
      // (35-40 al minuto in produzione), e ogni giro spegneva il riquadro
      // rosso del salvataggio fallito prima che chi salvava lo leggesse.
      if (JSON.stringify(draft.definition) === JSON.stringify(definition)) return;
      // keep instance/ops/dirty — only the DEFINITION converges to engine truth
      draft.definition = cloneDefinition(definition);
    },

    /**
     * C4 — Add a node from the LIBRARY to the draft (the "+" in the sidebar).
     * The draft gets a rendering clone built from the library node's projection;
     * a matching `add_node` op is queued (the engine constructs the REAL node
     * from its class at save — this clone is presentational until the refetch).
     * Client id = catalog id + first-free `_2/_3…` suffix (deterministic, same
     * rule as the engine, which validates instead of renaming).
     */
    addDraftNode: (
      state,
      action: PayloadAction<{ type: string; libraryNode: WorkflowLibraryNode; after?: string }>
    ) => {
      const { type, libraryNode, after } = action.payload;
      const draft = state.byType[type];
      if (!draft) return;
      const taken = new Set<string>();
      for (const n of draft.definition.nodes) {
        taken.add(n.id);
        for (const s of n.body ?? []) taken.add(s.id);
      }
      let id = libraryNode.id;
      for (let i = 2; taken.has(id); i++) id = `${libraryNode.id}_${i}`;
      // label: pretty-numbered when taken — mirrors the engine's fallback so
      // label-derived output labels ("<label> · Response") stay unique by
      // construction. ONE namespace with the bodies: a loop sub-node's picker
      // sees outer handles too (one-name-per-variable law).
      const labels = new Set<string | undefined>();
      for (const n of draft.definition.nodes) {
        labels.add(n.label);
        for (const sb of n.body ?? []) labels.add(sb.label);
      }
      let label = libraryNode.label;
      for (let i = 2; labels.has(label); i++) label = `${libraryNode.label} ${i}`;

      const node: WorkflowDefinitionNode = {
        id,
        label,
        node_type: libraryNode.id,            // client-only stamp (generic-brick affordances)
        wirable_inputs: libraryNode.wirable_inputs ?? [],
        user_named_outputs: libraryNode.user_named_outputs ?? false,
        engine: libraryNode.engine,
        engine_label: libraryNode.engine_label,
        system_prompt: libraryNode.system_prompt ?? "",
        user_prompt: libraryNode.user_prompt ?? "",
        model: libraryNode.model,
        web_search: libraryNode.web_search,
        inputs: JSON.parse(JSON.stringify(libraryNode.inputs ?? [])),
        outputs: JSON.parse(JSON.stringify(libraryNode.outputs ?? [])),
        options: JSON.parse(JSON.stringify(libraryNode.options ?? [])),
        logic: libraryNode.logic ?? "",
        order: draft.definition.nodes.length,
        output_label: "",
        depends_on: [],
      } as WorkflowDefinitionNode;

      const idx = after ? draft.definition.nodes.findIndex((n) => n.id === after) : -1;
      if (idx >= 0) draft.definition.nodes.splice(idx + 1, 0, node);
      else draft.definition.nodes.push(node);

      const op: WorkflowOp = { op: "add_node", node_type: libraryNode.id, id, label };
      if (after) (op as { after?: string }).after = after;
      draft.pendingOps.push(op);
      draft.dirty = true;
    },

    /** C4 — Add a node INSIDE a loop's body (the container's "+ Add a node").
     *  Optimistic mirror: the sub-node clone lands in `loop.body`; the intent
     *  travels as `add_node {in_body}` — the engine builds the REAL sub-node,
     *  schedules it inside the iteration and reconciles the collected output. */
    addDraftNodeInBody: (
      state,
      action: PayloadAction<{ type: string; loopId: string;
                              libraryNode: WorkflowLibraryNode; after?: string }>
    ) => {
      const { type, loopId, libraryNode, after } = action.payload;
      const draft = state.byType[type];
      const loop = draft && findNode(draft, loopId);
      if (!draft || !loop) return;
      const taken = new Set<string>();
      for (const n of draft.definition.nodes) {
        taken.add(n.id);
        for (const sb of n.body ?? []) taken.add(sb.id);
      }
      let id = libraryNode.id;
      for (let i = 2; taken.has(id); i++) id = `${libraryNode.id}_${i}`;
      // same ONE label namespace as top-level adds (see addDraftNode)
      const takenLabels = new Set<string | undefined>();
      for (const n of draft.definition.nodes) {
        takenLabels.add(n.label);
        for (const sb of n.body ?? []) takenLabels.add(sb.label);
      }
      let subLabel = libraryNode.label;
      for (let i = 2; takenLabels.has(subLabel); i++) subLabel = `${libraryNode.label} ${i}`;

      const sub: WorkflowDefinitionNode = {
        id,
        label: subLabel,
        node_type: libraryNode.id,
        wirable_inputs: libraryNode.wirable_inputs ?? [],
        user_named_outputs: libraryNode.user_named_outputs ?? false,
        engine: libraryNode.engine,
        engine_label: libraryNode.engine_label,
        system_prompt: libraryNode.system_prompt ?? "",
        user_prompt: libraryNode.user_prompt ?? "",
        model: libraryNode.model,
        web_search: libraryNode.web_search,
        inputs: JSON.parse(JSON.stringify(libraryNode.inputs ?? [])),
        outputs: JSON.parse(JSON.stringify(libraryNode.outputs ?? [])),
        options: JSON.parse(JSON.stringify(libraryNode.options ?? [])),
        logic: libraryNode.logic ?? "",
        order: (loop.body ?? []).length,
        output_label: "",
        depends_on: [],
      } as WorkflowDefinitionNode;

      loop.body = loop.body ?? [];
      const idx = after ? loop.body.findIndex((n) => n.id === after) : -1;
      if (idx >= 0) loop.body.splice(idx + 1, 0, sub);
      else loop.body.push(sub);

      const op: WorkflowOp = { op: "add_node", node_type: libraryNode.id, id,
                               label: sub.label } as WorkflowOp;
      (op as { in_body?: string }).in_body = loopId;
      if (after) (op as { after?: string }).after = after;
      draft.pendingOps.push(op);
      draft.dirty = true;
    },

    /**
     * C4 — Remove a node from the draft. NORMALIZATION: if the id was born from a
     * pending `add_node` (never persisted), the add and every op touching the id
     * are simply cancelled — no `remove_node` travels. Otherwise the node leaves
     * the draft (deps scrubbed for rendering) and a `remove_node` op is queued;
     * the engine re-does the scrub authoritatively and the linter reports any
     * dangling DATA refs (the pre-save impact warning is the UI's job).
     */
    removeDraftNode: (state, action: PayloadAction<{ type: string; nodeId: string }>) => {
      const { type, nodeId } = action.payload;
      const draft = state.byType[type];
      if (!draft) return;
      const victimTop = draft.definition.nodes.find((n) => n.id === nodeId);
      const exists = Boolean(victimTop);
      if (!exists) {
        // a loop-body SUB-NODE: remove it from its container's body (the engine's
        // remove_node already searches bodies — the mirror must too)
        const container = draft.definition.nodes.find(
          (n) => (n.body ?? []).some((sb) => sb.id === nodeId));
        if (!container) return;
        container.body = (container.body ?? []).filter((sb) => sb.id !== nodeId);
      }

      draft.definition.nodes = draft.definition.nodes.filter((n) => n.id !== nodeId);
      for (const n of draft.definition.nodes) {
        if (n.depends_on?.includes(nodeId)) {
          n.depends_on = n.depends_on.filter((d) => d !== nodeId);
        }
      }

      const wasPendingAdd = draft.pendingOps.some(
        (o) => o.op === "add_node" && o.id === nodeId
      );
      // a CONTAINER dies with its whole body: ops touching its sub-nodes would
      // otherwise linger as orphans (an add_node {in_body} whose target is gone
      // rejects every later validate/save)
      const gone = new Set([nodeId]);
      for (const sb of victimTop?.body ?? []) gone.add(sb.id);
      draft.pendingOps = draft.pendingOps.filter((o) => {
        const oid = (o as { id?: string }).id;
        const onode = (o as { node?: string }).node;
        const oon = (o as { on?: string }).on;
        const inBody = (o as { in_body?: string }).in_body;
        if (o.op === "add_node" && (gone.has(oid ?? "") || gone.has(inBody ?? ""))) return false;
        if ((o.op === "wire" || o.op === "unwire" || o.op === "set_ports") && gone.has(onode ?? "")) return false;
        if ((o.op === "attach_branch" || o.op === "detach_branch") && gone.has(onode ?? "")) return false;
        if ((o.op === "add_dep" || o.op === "remove_dep") && (gone.has(onode ?? "") || gone.has(oon ?? ""))) return false;
        if ((o.op === "set_chapters" || o.op === "rename_output_label") && gone.has(onode ?? "")) return false;
        if (o.op === "remove_node" && gone.has(oid ?? "")) return false;
        return true;
      });
      if (!wasPendingAdd) draft.pendingOps.push({ op: "remove_node", id: nodeId });
      // Un capitolo che leggeva il nodo cancellato non ha piu' una sorgente:
      // l'End lo perde SUBITO, mirror e op insieme. Lasciato li', il
      // `set_chapters` pendente citava un nodo inesistente e l'engine
      // rifiutava l'INTERO batch — un errore «Ops» che nessun pannello sapeva
      // correggere, e che ricompariva a ogni nodo cancellato (svinando,
      // 2026-09-22). Gli altri input pendenti restano al linter, come nel
      // remove_node dell'engine: sono una scelta dell'autore, non nostra.
      for (const n of draft.definition.nodes) {
        if (!isEndNode(n)) continue;
        const kept = (n.inputs ?? [])
          .map((i) => {
            const m = /\{\{\s*([^}]+?)\s*\}\}/.exec(i.description ?? "");
            return m ? { ref: m[1].trim(), title: i.label || i.key } : null;
          })
          .filter((c): c is { ref: string; title: string } => Boolean(c));
        const survivors = kept.filter((c) => !gone.has(producerOf(c.ref) ?? ""));
        if (survivors.length !== kept.length) applyChapters(draft, n, survivors);
      }
      draft.dirty = true;
    },

    /**
     * C4 — Rename the GENERIC Prompt LLM's primary output to a user-semantic key
     * (the "variable name" field in the LLM panel). Same atomic cascade as
     * renameCodeOutput (every downstream `{{nodes.<id>.<oldKey>}}` rewritten),
     * plus a coalesced `set_ports` op so the rename PERSISTS. `results` is fixed.
     */
    renameLlmOutput: (
      state,
      action: PayloadAction<{ type: string; nodeId: string; newKey: string }>
    ) => {
      const { type, nodeId, newKey } = action.payload;
      const draft = state.byType[type];
      if (!draft) return;
      const node = findNode(draft, nodeId);
      if (!node) return;
      if (!isValidKey(newKey) || newKey === "results") return;
      const primary = (node.outputs ?? []).find((o) => o.key !== "results");
      if (!primary || primary.key === newKey) return;
      if ((node.outputs ?? []).some((o) => o.key === newKey)) return;

      const oldKey = primary.key;
      primary.key = newKey;
      primary.label = newKey;                    // one name per variable (§3.13)
      rewriteRefsAcrossDraft(draft, nodeId, oldKey, newKey);

      // the KEY rename supersedes any pending cosmetic rename of this node's
      // output — a stale rename_output_label (queued by the old mis-routing)
      // would keep the whole save rejected forever (autosave preserves ops now)
      draft.pendingOps = draft.pendingOps.filter(
        (o) => !(o.op === "rename_output_label" && o.node === nodeId));
      upsertOp(draft, {
        op: "set_ports", node: nodeId, outputs: [{ key: newKey }],
      });
      draft.dirty = true;
    },

    /**
     * UX — insert a node ON an edge (the "+" circle): the node lands after
     * `source` with a SEQUENCING dep, and (when the edge points at a real node)
     * the target gains a dep on the new node — so the spine renders A→new→B
     * immediately. Data wiring is the user's next step (Profound behaves the
     * same: the insert is control-flow; the prompt/inputs carry the data).
     */
    insertDraftNodeOnEdge: (
      state,
      action: PayloadAction<{ type: string; libraryNode: WorkflowLibraryNode;
                              source: string; target?: string | null }>
    ) => {
      const { type, libraryNode, source, target } = action.payload;
      const draft = state.byType[type];
      if (!draft) return;
      const taken = new Set<string>();
      for (const n of draft.definition.nodes) {
        taken.add(n.id);
        for (const s of n.body ?? []) taken.add(s.id);
      }
      let id = libraryNode.id;
      for (let i = 2; taken.has(id); i++) id = `${libraryNode.id}_${i}`;
      const labels = new Set(draft.definition.nodes.map((n) => n.label));
      let label = libraryNode.label;
      for (let i = 2; labels.has(label); i++) label = `${libraryNode.label} ${i}`;

      const node: WorkflowDefinitionNode = {
        id, label,
        node_type: libraryNode.id,
        wirable_inputs: libraryNode.wirable_inputs ?? [],
        user_named_outputs: libraryNode.user_named_outputs ?? false,
        engine: libraryNode.engine,
        engine_label: libraryNode.engine_label,
        system_prompt: libraryNode.system_prompt ?? "",
        user_prompt: libraryNode.user_prompt ?? "",
        model: libraryNode.model,
        web_search: libraryNode.web_search,
        inputs: JSON.parse(JSON.stringify(libraryNode.inputs ?? [])),
        outputs: JSON.parse(JSON.stringify(libraryNode.outputs ?? [])),
        options: JSON.parse(JSON.stringify(libraryNode.options ?? [])),
        logic: libraryNode.logic ?? "",
        order: draft.definition.nodes.length,
        output_label: "",
        depends_on: [source],
      } as WorkflowDefinitionNode;

      const idx = draft.definition.nodes.findIndex((n) => n.id === source);
      draft.definition.nodes.splice(idx >= 0 ? idx + 1 : draft.definition.nodes.length, 0, node);
      if (target) {
        const t = draft.definition.nodes.find((n) => n.id === target);
        if (t) {
          t.depends_on = t.depends_on ?? [];
          if (!t.depends_on.includes(id)) t.depends_on.push(id);
        }
      }
      draft.pendingOps.push({ op: "add_node", node_type: libraryNode.id, id, label, after: source });
      draft.pendingOps.push({ op: "add_dep", node: id, on: source });
      if (target && draft.definition.nodes.some((n) => n.id === target)) {
        draft.pendingOps.push({ op: "add_dep", node: target, on: id });
      }
      draft.dirty = true;
    },

    /**
     * TD-02 — insert a node ON A GATE'S BRANCH (the canvas "+ if true / + if
     * false" ghost stubs): the node lands after the gate carrying
     * branch_deps {gate: leg}, and `attach_branch` travels with the
     * `add_node` (the engine's _op_attach_branch re-derives the scheduling
     * dep authoritatively). The fork opens visually at once — the C2 layout
     * reads branchDeps.
     */
    insertDraftNodeOnBranch: (
      state,
      action: PayloadAction<{ type: string; libraryNode: WorkflowLibraryNode;
                              gate: string; leg: "true" | "false" }>
    ) => {
      const { type, libraryNode, gate, leg } = action.payload;
      const draft = state.byType[type];
      if (!draft) return;
      const gateNode = draft.definition.nodes.find((n) => n.id === gate);
      if (!gateNode) return;
      if (!(gateNode.outputs ?? []).some((o) => o.key === "branch")) return;
      if (leg === "false" && gateNode.false_exits) return; // engine rejects it too

      const taken = new Set<string>();
      for (const n of draft.definition.nodes) {
        taken.add(n.id);
        for (const s of n.body ?? []) taken.add(s.id);
      }
      let id = libraryNode.id;
      for (let i = 2; taken.has(id); i++) id = `${libraryNode.id}_${i}`;
      const labels = new Set(draft.definition.nodes.map((n) => n.label));
      let label = libraryNode.label;
      for (let i = 2; labels.has(label); i++) label = `${libraryNode.label} ${i}`;

      const node: WorkflowDefinitionNode = {
        id, label,
        node_type: libraryNode.id,
        wirable_inputs: libraryNode.wirable_inputs ?? [],
        user_named_outputs: libraryNode.user_named_outputs ?? false,
        engine: libraryNode.engine,
        engine_label: libraryNode.engine_label,
        system_prompt: libraryNode.system_prompt ?? "",
        user_prompt: libraryNode.user_prompt ?? "",
        model: libraryNode.model,
        web_search: libraryNode.web_search,
        inputs: JSON.parse(JSON.stringify(libraryNode.inputs ?? [])),
        outputs: JSON.parse(JSON.stringify(libraryNode.outputs ?? [])),
        options: JSON.parse(JSON.stringify(libraryNode.options ?? [])),
        logic: libraryNode.logic ?? "",
        order: draft.definition.nodes.length,
        output_label: "",
        depends_on: [gate],
        branch_deps: { [gate]: leg },
      } as WorkflowDefinitionNode;

      const idx = draft.definition.nodes.findIndex((n) => n.id === gate);
      draft.definition.nodes.splice(idx >= 0 ? idx + 1 : draft.definition.nodes.length, 0, node);

      draft.pendingOps.push({ op: "add_node", node_type: libraryNode.id, id, label, after: gate });
      draft.pendingOps.push({ op: "attach_branch", node: id, gate, leg });
      draft.dirty = true;
    },

    /**
     * UX — swap the Start's declared run-parameter fields (the "Add input
     * field" modal + per-field ✕). Client mirror of the engine's MERGE-REBUILD:
     * kept fields keep their edited option defaults + renamed labels; new
     * fields come from the registry metadata. One coalesced op.
     */
    setStartFields: (
      state,
      action: PayloadAction<{ type: string; fields: (string | CustomStartField)[];
                              registry: StartFieldMeta[] }>
    ) => {
      const { type, registry } = action.payload;
      const draft = state.byType[type];
      const start = draft && findNode(draft, "start");
      if (!draft || !start) return;   // [] is valid: an empty Start asks for nothing
      const byKey = new Map(registry.map((f) => [f.key, f]));
      // SELF-HEALING for degraded provenance: a custom field can reach us as a
      // bare KEY string when a hand-rebuilt panel node dropped `start_fields`
      // (blank-canvas incident: the second "Add input field" became a silent
      // no-op). The node's own options/outputs still carry the full spec —
      // recover it instead of nuking the whole action. Only a key with no
      // trace anywhere is a real FE bug: that single entry is dropped, loudly
      // in dev, and everything else still applies (never-silent, never-frozen).
      const CONTROL2TYPE: Record<string, CustomStartField["type"]> = {
        text: "text", number: "number", time_range: "time_range",
        select: "select", toggle: "boolean",
      };
      const recover = (k: string): CustomStartField | null => {
        const opt = (start.options ?? []).find((o) => o.key === k);
        const out = (start.outputs ?? []).find((p) => p.key === k);
        if (!opt && !out) return null;
        const control = String((opt as { type?: string } | undefined)?.type ?? "text");
        const t: CustomStartField["type"] = control === "textarea"
          ? (out?.type === "list" ? "list" : "textarea")
          : (CONTROL2TYPE[control] ?? "text");
        return {
          key: k,
          label: (opt?.label ?? out?.label ?? k) as string,
          type: t,
          required: Boolean((opt as { required?: boolean } | undefined)?.required),
          ...(t === "select" ? { choices: ((opt?.choices ?? []) as { value: string; label: string }[])
              .map((c) => [c.value, c.label] as [string, string]) } : {}),
        };
      };
      const fields = action.payload.fields
        .map((f) => (typeof f === "string" && !byKey.has(f) ? (recover(f) ?? f) : f))
        .filter((f, _i, _a) => {
          const unknown = typeof f === "string" && !byKey.has(f);
          if (unknown && process.env.NODE_ENV !== "production") {
            console.error("[workflowDraft] setStartFields: unrecoverable field key", f);
          }
          return !unknown;
        });
      // the mirror of the engine's slug (normalize_custom_field): key ← label
      const slug = (label: string) => {
        const k = label.toLowerCase().replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "").slice(0, 64) || "field";
        return /^[a-z]/.test(k) ? k : `f_${k}`.slice(0, 64);
      };
      // field type → (form CONTROL, produced VARIABLE type) — engine mirror
      const CUSTOM: Record<string, [string, PortSpec["type"]]> = {
        text: ["text", "text"], textarea: ["textarea", "text"],
        number: ["number", "number"], time_range: ["time_range", "number"],
        list: ["textarea", "list"], select: ["select", "text"],
        boolean: ["toggle", "boolean"],
      };
      const keyOf = (f: string | CustomStartField) =>
        typeof f === "string" ? f : (f.key || slug(f.label));

      const oldOut = new Map((start.outputs ?? []).map((p) => [p.key, p]));
      const oldOpt = new Map((start.options ?? []).map((o) => [o.key, o]));
      // KEPT = same key AND same KIND (engine mirror): a custom whose slug
      // collides with a removed registry key is a NEW field — restoring the old
      // registry label over it stole its identity ("URL" read back as "URL to
      // analyze"). For customs the spec is the user's latest intent.
      const oldProv: (string | CustomStartField)[] =
        (start as { start_fields?: (string | CustomStartField)[] }).start_fields ?? [];
      const kindOf = (list: (string | CustomStartField)[], k: string) => {
        const hit = list.find((f) => keyOf(f) === k);
        return hit === undefined ? undefined : (typeof hit === "string" ? "registry" : "custom");
      };
      const sameKind = (k: string) =>
        oldProv.length === 0 || kindOf(oldProv, k) === kindOf(fields, k);

      start.outputs = fields.map((f) => {
        const k = keyOf(f);
        const kept = sameKind(k) ? oldOut.get(k) : undefined;
        if (typeof f === "string") {
          const meta = byKey.get(f)!;
          return { key: k, type: meta.out_type, label: kept?.label ?? meta.label };
        }
        return { key: k, type: CUSTOM[f.type][1], label: f.label };
      });
      start.options = fields.map((f) => {
        const k = keyOf(f);
        const kept = sameKind(k) ? oldOpt.get(k) : undefined;
        if (kept && typeof f === "string") return kept;  // registry kept → edits survive
        if (kept && typeof f !== "string") {
          // custom kept → typed default survives, the SPEC drives the rest
          return {
            key: k, label: f.label, type: CUSTOM[f.type][0] as never,
            default: kept.default ?? ((f.choices?.[0]?.[0] ?? "") as OptionValue),
            required: Boolean(f.required),
            choices: (f.choices ?? []).map(([value, label]) => ({ value, label })),
          } as unknown as NonNullable<WorkflowDefinitionNode["options"]>[number];
        }
        if (typeof f === "string") {
          const meta = byKey.get(f)!;
          return {
            key: k, label: meta.label, type: meta.type as OptionValue as never,
            default: (meta.choices?.[0]?.[0] ?? "") as OptionValue,
            help: meta.help,
            choices: (meta.choices ?? []).map(([value, label]) => ({ value, label })),
            visible_when: meta.visible_when ?? undefined,
            prompt_scope: (meta.prompt_scope ?? undefined) as never,
            persona_key: meta.persona_key ?? undefined,
            max_select: meta.max_select ?? undefined,
          } as unknown as NonNullable<WorkflowDefinitionNode["options"]>[number];
        }
        return {
          key: k, label: f.label, type: CUSTOM[f.type][0] as never,
          default: (f.choices?.[0]?.[0] ?? "") as OptionValue,
          required: Boolean(f.required),
          choices: (f.choices ?? []).map(([value, label]) => ({ value, label })),
        } as unknown as NonNullable<WorkflowDefinitionNode["options"]>[number];
      });
      // provenance for the NEXT rebuild (mirrors the engine projection)
      (start as { start_fields?: (string | CustomStartField)[] }).start_fields = fields;
      upsertOp(draft, { op: "set_start_fields", fields });
      draft.dirty = true;
    },

    /**
     * UX — rename a variable's user-facing LABEL (any standard brick's output;
     * Code + the generic Prompt LLM rename the KEY instead — label==key LAW).
     * Consumer chips re-derive from availableHandles; on the Start the matching
     * OPTION label follows (the run form must agree with the pill).
     */
    renameOutputLabel: (
      state,
      action: PayloadAction<{ type: string; nodeId: string; key: string; label: string }>
    ) => {
      const { type, nodeId, key } = action.payload;
      const label = action.payload.label.trim();
      const draft = state.byType[type];
      const node = draft && findNode(draft, nodeId);
      if (!draft || !node || !label) return;
      if (isCodeNode(node)) return;                          // key-rename path
      const port = (node.outputs ?? []).find((p) => p.key === key);
      if (!port || port.label === label) return;
      // one name per variable — a colliding label would 422 anyway; block early
      const collision = draft.definition.nodes.some((n) =>
        (n.outputs ?? []).some((p) => p.label === label && !(n.id === nodeId && p.key === key)));
      if (collision) return;
      port.label = label;
      if (nodeId === "start") {
        const opt = (node.options ?? []).find((o) => o.key === key);
        if (opt) opt.label = label;
      }
      // CASCADE (user feedback): every consumer input reading this variable shows
      // the new name IMMEDIATELY in the draft — mirror of the engine aligner.
      // OutputNode consumers keep their titles (chapter headings are user-owned).
      const target = `nodes.${nodeId}.${key}`;
      for (const n of draft.definition.nodes) {
        if ((n.outputs ?? []).some((p) => p.key === "document")) continue;
        for (const inp of n.inputs ?? []) {
          if ((inp.description ?? "").includes(target)) inp.label = label;
        }
        for (const sub of n.body ?? []) {
          for (const inp of sub.inputs ?? []) {
            if ((inp.description ?? "").includes(target)) inp.label = label;
          }
        }
      }
      upsertOp(draft, { op: "rename_output_label", node: nodeId, output: key, label });
      draft.dirty = true;
    },

    /**
     * UX — wire a standard brick's W2 input ({{ref}} → bare `*_ref` config slot,
     * e.g. fetch_page `url`/`urls`, citation_pages `analyze`): queue the `wire`
     * op (the engine writes config + port + dep) and MIRROR the input port +
     * dep in the draft so the panel/canvas update instantly.
     */
    wireDraftInput: (
      state,
      action: PayloadAction<{ type: string; nodeId: string; input: string;
                              ref: string; label?: string }>
    ) => {
      const { type, nodeId, input, ref, label } = action.payload;
      const draft = state.byType[type];
      const node = draft && findNode(draft, nodeId);
      if (!draft || !node) return;
      const bare = ref.replace(/^\{\{\s*|\s*\}\}$/g, "");
      node.inputs = node.inputs ?? [];
      const port = node.inputs.find((p) => p.key === input);
      if (port) {
        port.description = `From {{${bare}}}`;
        if (label) port.label = label;
      } else {
        node.inputs.push({ key: input, type: "json" as PortSpec["type"],
                           label: label ?? input, description: `From {{${bare}}}` });
      }
      const m = /^nodes\.([A-Za-z0-9_]+)\./.exec(bare);
      if (m && m[1] !== nodeId) {
        node.depends_on = node.depends_on ?? [];
        if (!node.depends_on.includes(m[1])) node.depends_on.push(m[1]);
      }
      // coalesce per (node, input): the latest wire wins; drop a prior unwire
      draft.pendingOps = draft.pendingOps.filter((o) =>
        !((o.op === "wire" || o.op === "unwire") && o.node === nodeId && o.input === input));
      draft.pendingOps.push({ op: "wire", node: nodeId, input, ref: bare });
      draft.dirty = true;
    },

    /** UX — disconnect a W2 input (reverse of wireDraftInput). */
    unwireDraftInput: (
      state,
      action: PayloadAction<{ type: string; nodeId: string; input: string }>
    ) => {
      const { type, nodeId, input } = action.payload;
      const draft = state.byType[type];
      const node = draft && findNode(draft, nodeId);
      if (!draft || !node) return;
      const port = (node.inputs ?? []).find((p) => p.key === input);
      const bare = port?.description?.match(/\{\{\s*([^}]+?)\s*\}\}/)?.[1];
      node.inputs = (node.inputs ?? []).filter((p) => p.key !== input);
      if (bare) {
        const pid = /^nodes\.([A-Za-z0-9_]+)\./.exec(bare)?.[1];
        // drop the dep only if nothing else on this node still reads that producer
        if (pid) {
          const still = [node.system_prompt, node.user_prompt, node.code_python,
            node.code_javascript, ...(node.inputs ?? []).map((i) => i.description)]
            .some((t) => typeof t === "string" && t.includes(`nodes.${pid}.`));
          if (!still) node.depends_on = (node.depends_on ?? []).filter((d) => d !== pid);
        }
      }
      const hadPendingWire = draft.pendingOps.some(
        (o) => o.op === "wire" && o.node === nodeId && o.input === input);
      draft.pendingOps = draft.pendingOps.filter((o) =>
        !((o.op === "wire" || o.op === "unwire") && o.node === nodeId && o.input === input));
      if (!hadPendingWire) {
        draft.pendingOps.push({ op: "unwire", node: nodeId, input });
      }
      draft.dirty = true;
    },

    /**
     * UX — the End card's output picker: replace the deliverable's chapters
     * wholesale. Mirrors the engine rebuild: inputs from chapters (label =
     * title), deps = chapter producers + preserved sequencing deps.
     */
    setDraftChapters: (
      state,
      action: PayloadAction<{ type: string; nodeId: string;
                              chapters: { ref: string; title?: string }[] }>
    ) => {
      const { type, nodeId, chapters } = action.payload;
      const draft = state.byType[type];
      const node = draft && findNode(draft, nodeId);
      if (!draft || !node) return;
      applyChapters(draft, node, chapters);
    },

    /**
     * Rename a Code node's output key — the ATOMIC cascade refactor.
     * Renames the output AND rewrites every downstream `{{nodes.<id>.<oldKey>}}`
     * ref across the whole draft (prompts, code, input descriptions, input refs).
     * No-op (with a dev warning) on non-Code nodes — those keys are engine-owned.
     */
    renameCodeOutput: (
      state,
      action: PayloadAction<{ type: string; nodeId: string; oldKey: string; newKey: string }>
    ) => {
      const { type, nodeId, oldKey, newKey } = action.payload;
      const draft = state.byType[type];
      if (!draft) return;
      const node = findNode(draft, nodeId);
      if (!node || !isCodeNode(node)) return; // contract: only Code keys are user-owned
      if (oldKey === newKey) return;
      if (!isValidKey(newKey)) return;
      // unique among this node's outputs
      if ((node.outputs ?? []).some((o) => o.key === newKey)) return;

      // 1) rename the output port
      const port = (node.outputs ?? []).find((o) => o.key === oldKey);
      if (!port) return;
      port.key = newKey;
      port.label = newKey; // Code rule: label IS the key

      // 2) rewrite every downstream ref (the shared atomic cascade)
      const rewrites = rewriteRefsAcrossDraft(draft, nodeId, oldKey, newKey);

      // 3) invariant (dev only): no stale ref may survive a rename
      if (process.env.NODE_ENV !== "production") {
        const stale = draft.definition.nodes.some((n) =>
          [n.system_prompt, n.user_prompt, n.code_python, n.code_javascript,
            ...(n.inputs ?? []).map((i) => i.description)]
            .filter((s): s is string => typeof s === "string")
            .some((s) => referencesNodeOutput(s, nodeId, oldKey))
        );
        if (stale) {
          console.error(
            `[workflowDraft] renameCodeOutput left a STALE ref ${buildRef("nodes", oldKey, nodeId)} ` +
              `after ${rewrites} rewrite(s). This would raise VariableResolutionError at runtime.`
          );
        }
      }

      // 4) persist: the rename travels as a coalesced set_ports snapshot (C4)
      upsertOp(draft, codePortsSnapshot(node));
      draft.dirty = true;
    },

    /** Add a new Code-node output (name + ui-type). No-op on collision / invalid key. */
    addCodeOutput: (
      state,
      action: PayloadAction<{ type: string; nodeId: string; key: string; uiType: string }>
    ) => {
      const { type, nodeId, key, uiType } = action.payload;
      const draft = state.byType[type];
      const node = draft && findNode(draft, nodeId);
      if (!node || !isCodeNode(node)) return;
      if (!isValidKey(key)) return;
      node.outputs = node.outputs ?? [];
      if (node.outputs.some((o) => o.key === key)) return;
      node.outputs.push({ key, type: TYPE_FROM_UI[uiType] ?? "text", label: key });
      upsertOp(draft, codePortsSnapshot(node));
      draft.dirty = true;
    },

    /** Remove a Code-node output. (Downstream refs become orphan — the UI warns
     *  before allowing it; we still let the reducer remove the port.) */
    removeCodeOutput: (state, action: PayloadAction<{ type: string; nodeId: string; key: string }>) => {
      const { type, nodeId, key } = action.payload;
      const draft = state.byType[type];
      const node = draft && findNode(draft, nodeId);
      if (!node || !isCodeNode(node)) return;
      node.outputs = (node.outputs ?? []).filter((o) => o.key !== key);
      upsertOp(draft, codePortsSnapshot(node));
      draft.dirty = true;
    },

    /** Change a Code-node output's type. */
    setCodeOutputType: (
      state,
      action: PayloadAction<{ type: string; nodeId: string; key: string; uiType: string }>
    ) => {
      const { type, nodeId, key, uiType } = action.payload;
      const draft = state.byType[type];
      const node = draft && findNode(draft, nodeId);
      if (!node || !isCodeNode(node)) return;
      const port = (node.outputs ?? []).find((o) => o.key === key);
      if (port) {
        port.type = TYPE_FROM_UI[uiType] ?? "text";
        upsertOp(draft, codePortsSnapshot(node));
        draft.dirty = true;
      }
    },

    /** Edit a Code node's source body for ONE language. A Code node carries both
     *  code_python and code_javascript at once; the editor edits whichever the
     *  `language` option currently selects, leaving the other body untouched. */
    setCodeNodeCode: (
      state,
      action: PayloadAction<{ type: string; nodeId: string; language: "python" | "javascript"; code: string }>
    ) => {
      const { type, nodeId, language, code } = action.payload;
      const draft = state.byType[type];
      const node = draft && findNode(draft, nodeId);
      if (node && isCodeNode(node)) {
        if (language === "javascript") node.code_javascript = code;
        else node.code_python = code;
        draft.dirty = true;
      }
    },

    /** Set a Code-node input's source ref (the {{…}} a kwarg reads). Stored in the
     *  input's `description` as the "From {{…}}" convention the panel renders. */
    setCodeInputRef: (
      state,
      action: PayloadAction<{ type: string; nodeId: string; key: string; ref: string }>
    ) => {
      const { type, nodeId, key, ref } = action.payload;
      const draft = state.byType[type];
      const node = draft && findNode(draft, nodeId);
      if (!node || !isCodeNode(node)) return;
      const port = (node.inputs ?? []).find((i) => i.key === key);
      if (port) {
        port.description = `From ${ref}`;
        // keep the draft's dependency mirror coherent for the canvas fallback
        // layout (the engine recomputes deps authoritatively from the op)
        const m = /\{\{\s*nodes\.([A-Za-z0-9_]+)\./.exec(ref);
        if (m && m[1] !== nodeId) {
          node.depends_on = node.depends_on ?? [];
          if (!node.depends_on.includes(m[1])) node.depends_on.push(m[1]);
        }
        upsertOp(draft, codePortsSnapshot(node));
        draft.dirty = true;
      }
    },

    /** Edit a schema-driven option's VALUE. Its draft home is the option's
     *  `default` — the same single-source the engine projects config into, so
     *  toNodeOverrides/toWorkflowEdits diff it and the rename cascade rewrites
     *  any `{{ref}}` stored in it (page-local override state can't be reached
     *  by the cascade — the DataForSEO location class of divergence). */
    setNodeOption: (
      state,
      action: PayloadAction<{ type: string; nodeId: string; key: string; value: OptionValue }>
    ) => {
      const { type, nodeId, key, value } = action.payload;
      const draft = state.byType[type];
      const node = draft && findNode(draft, nodeId);
      const opt = node && (node.options ?? []).find((o) => o.key === key);
      if (!node || !opt) return;
      opt.default = value;
      // OPTIMISTIC MODAL-PORT reconciliation — the instant mirror of the engine's
      // apply_edits rules (codec.py, render_as/run_per_item blocks). Without it
      // the output pill renamed only when the ~1s validate round-trip adopted the
      // engine projection (walkthrough: "switching html/markdown lags a second").
      // The engine stays the authority: the adoption converges over this mirror.
      if (key === "render_as" && typeof value === "string") {
        const fmtKeys = new Set((opt.choices ?? []).map((c) => String(c.value)));
        if (fmtKeys.has(value)) {
          const FMT: Record<string, { type: PortSpec["type"]; label: string }> = {
            html: { type: "text", label: "Raw HTML" },
            markdown: { type: "text", label: "Markdown" },
            structured: { type: "json", label: "Parsed page" },
          };
          const fmt = FMT[value] ?? { type: "text" as const,
            label: value.charAt(0).toUpperCase() + value.slice(1) };
          node.outputs = (node.outputs ?? []).map((pt) => {
            if (!fmtKeys.has(pt.key) || pt.key === value) return pt;
            const old = pt.label ?? "";
            const label = old.includes("·")
              ? `${old.split("·")[0].trim()} · ${fmt.label}` : fmt.label;
            return { ...pt, key: value, type: fmt.type, label };
          });
        }
      }
      if (key === "run_per_item") {
        const outs = (node.outputs ?? []).filter((pt) => pt.key !== "results");
        if (value) {
          outs.push({ key: "results", type: "list",
                      label: `${node.label || "Prompt LLM"} · Per-item results` } as PortSpec);
        }
        node.outputs = outs;
      }
      // a wired option ref IS wiring — keep depends_on coherent (same rule as
      // prompts; the engine maintains the authoritative deps at save)
      if (typeof value === "string") {
        for (const r of parseRefs(value)) {
          if (r.namespace === "nodes" && r.nodeId && r.nodeId !== nodeId) {
            node.depends_on = node.depends_on ?? [];
            if (!node.depends_on.includes(r.nodeId)) node.depends_on.push(r.nodeId);
          }
        }
      }
      draft.dirty = true;
    },

    /** Edit a node's editable prompt fields (LLM/standard or Code). */
    setNodePrompt: (
      state,
      action: PayloadAction<{ type: string; nodeId: string; field: "system_prompt" | "user_prompt"; value: string }>
    ) => {
      const { type, nodeId, field, value } = action.payload;
      const draft = state.byType[type];
      const node = draft && findNode(draft, nodeId);
      if (node) {
        node[field] = value;
        // mirror the engine: a prompt ref IS the LLM wiring — keep the draft's
        // depends_on coherent so the canvas fallback layout places the node
        // (the engine maintains the authoritative deps at save)
        for (const r of parseRefs(value)) {
          if (r.namespace === "nodes" && r.nodeId && r.nodeId !== nodeId) {
            node.depends_on = node.depends_on ?? [];
            if (!node.depends_on.includes(r.nodeId)) node.depends_on.push(r.nodeId);
          }
        }
        draft.dirty = true;
      }
    },

  },
});

export const {
  initDraft,
  resetDraft,
  hydrateDrafts,
  markDraftSaved,
  setDraftFindings,
  adoptEngineDefinition,
  addDraftNode,
  addDraftNodeInBody,
  removeDraftNode,
  insertDraftNodeOnEdge,
  insertDraftNodeOnBranch,
  setStartFields,
  wireDraftInput,
  unwireDraftInput,
  renameOutputLabel,
  setDraftChapters,
  renameLlmOutput,
  renameCodeOutput,
  addCodeOutput,
  removeCodeOutput,
  setCodeOutputType,
  setCodeNodeCode,
  setCodeInputRef,
  setNodeOption,
  setNodePrompt,
} = slice.actions;

export default slice.reducer;

// ─── Selectors ───────────────────────────────────────────────

export const selectDraft = (state: RootState, type: string): WorkflowDraft | undefined =>
  state.workflowDraft.byType[type];

/** node_id → the WORST severity among its findings — drives the canvas
 *  "Action required" badge. Workflow-level findings (node_id null) excluded
 *  (they belong to the aggregated panel, no node to badge). */
export const selectDraftVerdict = (state: RootState, type: string): DraftVerdictState | undefined =>
  state.workflowDraft.verdictByType[type];

/** L'ordine in cui le nature contano: ciò che è rotto vince su ciò che manca,
 *  che vince su un consiglio. Una sola scala, usata dal cartellino sul canvas
 *  e dall'elenco nella barra, così le due non possono raccontare cose diverse. */
const KIND_RANK: Record<FindingKind, number> = { broken: 0, unfinished: 1, advice: 2 };

/** La natura di un rilievo, con un ripiego onesto: un engine più vecchio non
 *  la manda, e in quel caso la severità è l'unica cosa che sappiamo. */
export function kindOfFinding(f: DraftFinding): FindingKind {
  return f.kind ?? (f.severity === "error" ? "broken" : "advice");
}

/** node_id -> la natura PEGGIORE fra i suoi rilievi. I rilievi dell'intero
 *  workflow (node_id null) restano fuori: non c'è un nodo da marcare. */
export function worstKindByNode(
  findings: DraftFinding[]
): Record<string, FindingKind> {
  const out: Record<string, FindingKind> = {};
  for (const f of findings) {
    if (!f.node_id) continue;
    const k = kindOfFinding(f);
    const cur = out[f.node_id];
    if (!cur || KIND_RANK[k] < KIND_RANK[cur]) out[f.node_id] = k;
  }
  return out;
}

export const selectFindingSeverityByNode = (
  state: RootState,
  type: string
): Record<string, "error" | "warning"> => {
  const out: Record<string, "error" | "warning"> = {};
  for (const f of state.workflowDraft.verdictByType[type]?.findings ?? []) {
    if (!f.node_id) continue;
    if (f.severity === "error" || !out[f.node_id]) out[f.node_id] = f.severity;
  }
  return out;
};

/**
 * Il salvataggio deve essere bloccato dal verdetto CORRENTE, mai da quello di
 * un secondo fa.
 *
 * Il controllo vivo e' debounced: fra la correzione e la risposta dell'engine
 * la lista in memoria descrive ancora il documento PRIMA della correzione.
 * Bloccare su quella lista significa dire a chi ha appena risolto un errore
 * che quell'errore c'e' ancora. Mentre un controllo e' in volo la decisione
 * non spetta piu' al browser: si manda all'engine, che rivalida comunque e
 * risponde 422 con le findings VERE se davvero restano.
 *
 * Pura, quindi provabile: e' la regola, non il posto in cui e' scritta.
 */
export function blockingErrorCount(
  verdict: DraftVerdictState | undefined
): number {
  if (!verdict || verdict.checking) return 0;
  return verdict.findings.filter((f) => f.severity === "error").length;
}

export const selectDraftNode = (
  state: RootState,
  type: string,
  nodeId: string
): WorkflowDefinitionNode | undefined =>
  state.workflowDraft.byType[type]?.definition.nodes.find((n) => n.id === nodeId);

/**
 * PURE: every handle a node MAY reference, computed from a definition — the Start
 * namespaces + each upstream node's declared outputs (transitive over depends_on).
 * This is what the variable picker lists, so a pill can only ever produce a real,
 * in-scope `{{…}}` ref. Reusable from a selector OR directly from a component that
 * already holds the (draft or base) definition.
 */
export function computeAvailableHandles(
  def: WorkflowDefinition,
  nodeId: string
): AvailableHandle[] {
  // A sub-node lives inside a container's (IterationNode's) `body`, NOT in def.nodes.
  // Its scope is: everything the CONTAINER can see (its upstream closure) + the loop
  // scope {{iter.item}}/{{iter.index}} + the outputs of EARLIER sub-nodes in the body.
  const container = def.nodes.find(
    (n) => Array.isArray(n.body) && n.body.some((s) => s.id === nodeId)
  );
  if (container) {
    // Start from the container's own available handles (start + upstream nodes).
    const out = computeAvailableHandles(def, container.id);
    const seen = new Set(out.map((h) => h.ref));
    // The per-iteration loop scope — first-class declared handles (engine `iter` ns).
    for (const h of LOOP_HANDLES) {
      if (!seen.has(h.ref)) { out.push(h); seen.add(h.ref); }
    }
    // Earlier sibling sub-nodes' outputs (the body runs in order).
    const body = container.body ?? [];
    for (const sub of body) {
      if (sub.id === nodeId) break; // only nodes BEFORE this one in the chain
      for (const port of sub.outputs ?? []) {
        const ref = buildRef("nodes", port.key, sub.id);
        if (!seen.has(ref)) {
          out.push({
            ref, label: port.label || port.key, type: port.type, bounded: port.bounded,
            source: { kind: "node", id: sub.id, label: sub.label || sub.id },
          });
          seen.add(ref);
        }
      }
    }
    return out;
  }

  // SCOPE (ops model): a node may reference ANY node that is not itself and not
  // a DESCENDANT (someone that already runs after it — referencing those would
  // close a cycle). NOT just the current upstream closure: in the ops model the
  // prompt/wire IS what creates the dependency, so gating the picker on deps
  // that don't exist yet is a chicken-and-egg (a freshly added node saw nothing).
  // The engine derives depends_on from the refs at save; cycles stay impossible
  // because descendants are excluded here and re-checked server-side.
  const dependents = new Map<string, string[]>(); // producer -> who depends on it
  for (const n of def.nodes) {
    for (const d of n.depends_on ?? []) {
      if (!dependents.has(d)) dependents.set(d, []);
      dependents.get(d)!.push(n.id);
    }
  }
  const descendants = new Set<string>();
  const stack = [...(dependents.get(nodeId) ?? [])];
  while (stack.length) {
    const id = stack.pop()!;
    if (descendants.has(id)) continue;
    descendants.add(id);
    stack.push(...(dependents.get(id) ?? []));
  }
  const out: AvailableHandle[] = [];
  for (const n of def.nodes) {
    if (n.id === nodeId || descendants.has(n.id)) continue;
    // the deliverable's `document` is the workflow's END — never an input elsewhere
    for (const port of (n.outputs ?? []).filter((p) => p.key !== "document")) {
      out.push({
        ref: buildRef("nodes", port.key, n.id),
        label: port.label || port.key,
        type: port.type,
        bounded: port.bounded,
        source: { kind: n.id === "start" ? "start" : "node", id: n.id, label: n.label || n.id },
      });
    }
  }
  // Fallback handles — ONLY for defs with no real start node at all (legacy
  // views). When a REAL start exists, its declared outputs are the ONLY start
  // handles: offering the hardcoded list would resurrect fields the user
  // REMOVED via set_start_fields (a phantom pill that 422s the moment it's
  // used — the audit's red-pill class).
  const hasRealStart = def.nodes.some((n) => n.id === "start");
  const seen = new Set(out.map((h) => h.ref));
  for (const h of START_HANDLES) {
    if (hasRealStart && h.ref.startsWith("{{nodes.start.")) continue;
    if (!seen.has(h.ref)) { out.push(h); seen.add(h.ref); }
  }
  return out;
}

/**
 * EVERY node's outputs across the whole definition (except `excludeId`'s own) —
 * what the End card's "Add output" picker lists: the user may collect ANY
 * produced variable into the deliverable, not just upstream ones (the engine
 * derives the deps from the chapters).
 */
export function computeAllNodeOutputHandles(
  def: WorkflowDefinition,
  excludeId: string
): AvailableHandle[] {
  const out: AvailableHandle[] = [];
  for (const n of def.nodes) {
    if (n.id === excludeId) continue;
    for (const port of n.outputs ?? []) {
      out.push({
        ref: buildRef("nodes", port.key, n.id),
        label: port.label || port.key,
        type: port.type,
        bounded: port.bounded,
        source: { kind: n.id === "start" ? "start" : "node", id: n.id, label: n.label || n.id },
      });
    }
  }
  return out;
}

/** Selector form: handles for a node from the live DRAFT (falls back to Start only
 *  when no draft exists yet). */
export function selectAvailableHandles(
  state: RootState,
  type: string,
  nodeId: string
): AvailableHandle[] {
  const draft = state.workflowDraft.byType[type];
  if (!draft) return [...START_HANDLES];
  return computeAvailableHandles(draft.definition, nodeId);
}

/**
 * Serialize the draft's edits into the `nodeOverrides` map `startAudit` sends
 * (Option A run path).
 *
 * IMPORTANT — what the engine actually applies at run time (dag_runner): ONLY the
 * per-node prompt/model/temperature/web_search overrides and the declared
 * schema-driven OPTIONS. There is NO runtime channel for a Code node's structural
 * contract (code / outputs / inputs) — the engine builds those from the registry
 * definition. So in Option A we serialize ONLY the prompt edits here; structural
 * Code-node edits live in the draft and become effective on a run via Option B
 * ("duplicate into my account" → a data-driven definition→nodes builder). See
 * `hasStructuralEdits` for the UI hint, and docs/workflow-editor-redesign-plan.md.
 *
 * Note: prompt edits already flow through the page's `nodeOverrides` channel too;
 * including them here keeps the draft authoritative and harmless to merge (shallow).
 */
export function toNodeOverrides(
  draft: WorkflowDraft,
  base: WorkflowDefinition
): Record<string, NodeOverride> {
  const baseById = new Map(base.nodes.map((n) => [n.id, n]));
  const overrides: Record<string, NodeOverride> = {};

  for (const node of draft.definition.nodes) {
    const b = baseById.get(node.id);
    if (!b) continue;
    const ov: NodeOverride = {};
    if (node.system_prompt !== b.system_prompt) ov.system_prompt = node.system_prompt;
    if (node.user_prompt !== b.user_prompt) ov.user_prompt = node.user_prompt;
    // Option values edited in the draft (setNodeOption). The page merges these
    // OVER its local override state, so a cascade-renamed `{{ref}}` inside an
    // option always ships fresh (the local copy can't be reached by the cascade).
    const baseOpts = new Map((b.options ?? []).map((o) => [o.key, o.default]));
    for (const o of node.options ?? []) {
      if (!baseOpts.has(o.key)) continue;
      if (JSON.stringify(o.default) !== JSON.stringify(baseOpts.get(o.key))) {
        (ov as Record<string, OptionValue>)[o.key] = o.default as OptionValue;
      }
    }
    if (Object.keys(ov).length) overrides[node.id] = ov;
  }
  return overrides;
}

/**
 * OPTION B — serialize the draft's edits into the persistent `edits` payload the
 * save endpoint (PUT /v1/domains/{id}/workflows/{type}) sends. Unlike toNodeOverrides
 * (ephemeral, per-run, prompts only), this carries the FULL set of fields the engine's
 * apply_edits allow-list accepts and writes into the saved fork:
 *   system_prompt, user_prompt, model, temperature, web_search, output_schema,
 *   code_python, code_javascript, and each declared OPTION's value.
 *
 * It is a DIFF vs the base definition — only changed fields are sent, so an untouched
 * node produces no entry. Structural port changes (adding/renaming Code outputs or
 * inputs) are NOT included: the engine's edit allow-list writes fields, not ports, so
 * those remain a follow-up (see hasStructuralEdits / the redesign plan). The keys here
 * match the engine's _EDITABLE_DEF_FIELDS + option keys exactly.
 */
export function toWorkflowEdits(
  draft: WorkflowDraft,
  base: WorkflowDefinition
): Record<string, NodeOverride> {
  const baseById = new Map(base.nodes.map((n) => [n.id, n]));
  const edits: Record<string, NodeOverride> = {};

  for (const node of draft.definition.nodes) {
    const b = baseById.get(node.id);
    if (!b) continue;
    const ov: NodeOverride = {};

    // Scalar definition fields — send only when changed from the base.
    if (node.system_prompt !== b.system_prompt) ov.system_prompt = node.system_prompt;
    if (node.user_prompt !== b.user_prompt) ov.user_prompt = node.user_prompt;
    if ((node.model ?? "") !== (b.model ?? "")) ov.model = node.model;
    if ((node.output_schema ?? "") !== (b.output_schema ?? "")) ov.output_schema = node.output_schema;
    if ((node.web_search ?? false) !== (b.web_search ?? false)) ov.web_search = node.web_search;
    if ((node.temperature ?? 0) !== (b.temperature ?? 0)) ov.temperature = node.temperature;
    // Code bodies (both languages persist independently).
    if ((node.code_python ?? "") !== (b.code_python ?? "")) ov.code_python = node.code_python;
    if ((node.code_javascript ?? "") !== (b.code_javascript ?? "")) ov.code_javascript = node.code_javascript;

    // Schema-driven OPTION values — each option's effective value is its `default`
    // (what the panel edits). Send only options whose value changed from the base.
    const baseOpts = new Map((b.options ?? []).map((o) => [o.key, o.default]));
    for (const o of node.options ?? []) {
      if (!baseOpts.has(o.key)) continue;
      if (JSON.stringify(o.default) !== JSON.stringify(baseOpts.get(o.key))) {
        (ov as Record<string, OptionValue>)[o.key] = o.default as OptionValue;
      }
    }

    if (Object.keys(ov).length) edits[node.id] = ov;
  }
  return edits;
}

/**
 * C4 — the structural ops the save sends alongside the field edits. The engine
 * applies them (in order, atomically) onto the authoritative doc.
 */
export function toWorkflowOps(draft: WorkflowDraft): WorkflowOp[] {
  return draft.pendingOps;
}

/**
 * Whether the draft carries STRUCTURAL edits (added/removed nodes, port changes,
 * code body edits) beyond plain prompt edits. Drives the "Save persists structure"
 * UI hint. Pending ops ARE structural by definition.
 */
export function hasStructuralEdits(draft: WorkflowDraft, base: WorkflowDefinition): boolean {
  if (draft.pendingOps.length > 0) return true;
  const baseById = new Map(base.nodes.map((n) => [n.id, n]));
  return draft.definition.nodes.some((n) => {
    if (!isCodeNode(n)) return false;
    const b = baseById.get(n.id);
    if (!b) return false;
    return (
      (n.code_python ?? "") !== (b.code_python ?? "") ||
      (n.code_javascript ?? "") !== (b.code_javascript ?? "") ||
      JSON.stringify(n.outputs) !== JSON.stringify(b.outputs) ||
      JSON.stringify((n.inputs ?? []).map((i) => [i.key, i.description])) !==
        JSON.stringify((b.inputs ?? []).map((i) => [i.key, i.description]))
    );
  });
}

/** True if the field references any handle NOT in the available set — used by the
 *  UI to flag an orphan ref before a run (defense in depth on top of the cascade). */
export function selectOrphanRefs(
  state: RootState,
  type: string,
  nodeId: string,
  text: string
): string[] {
  const available = new Set(selectAvailableHandles(state, type, nodeId).map((h) => h.ref));
  return parseRefs(text)
    .filter((r) => r.namespace === "nodes") // only node refs need a producer in scope
    .map((r) => r.raw)
    .filter((raw) => !available.has(raw));
}

export type { OptionValue };
